import { RadarChart } from '@mantine/charts';
import dayjs from 'dayjs';

import { resolveSeriesColor } from '#core/lib/formatters/colors.ts';
import { formatNumber, inferNumberFormat } from '#core/lib/formatters/number-decimals.ts';

import type { ChartRendererProps } from '../types.ts';

/**
 * Renders radar chart.
 */
export function RadarChartRenderer({ config, width, height }: ChartRendererProps) {
  const firstRow = config.data.at(0) ?? {};
  const numericFields = Object.keys(firstRow).filter((k) => k !== config.xAxis && typeof firstRow[k] === 'number');

  const seriesFromConfig = config.series?.length
    ? config.series
    : numericFields.map((name) => ({ name, color: undefined, shade: undefined }));
  const radarSeries = seriesFromConfig.map((s, i) => ({
    name: s.name,
    color: resolveSeriesColor(s, i),
    opacity: 0.2,
  }));

  // Radar tooltip formats across all numeric fields — combine their values to
  // pick a single set of hints. RadarChart has no valueFormatter prop, so we
  // format through the underlying recharts Tooltip.
  const combinedValues = config.data.flatMap((row) =>
    numericFields.map((f) => row[f]).filter((v): v is number => typeof v === 'number'),
  );
  const valueHints = inferNumberFormat(combinedValues);
  const formatValue = (value: unknown) => (typeof value === 'number' ? formatNumber(value, valueHints) : String(value));

  return (
    <RadarChart
      data={config.data}
      dataKey={config.xAxis}
      h={height}
      polarAngleAxisProps={{
        tickFormatter(value: number | string | Date) {
          const valueIsDate = value instanceof Date || (typeof value === 'string' && !Number.isNaN(Date.parse(value)));
          if (valueIsDate) {
            return dayjs(new Date(value)).format('YYYY-MM-DD');
          }
          return value.toString();
        },
      }}
      series={radarSeries}
      tooltipProps={{ formatter: formatValue }}
      w={width}
      withLegend
      withPolarAngleAxis
      withPolarGrid
      withTooltip
    />
  );
}
